import React from "react"
import styled from "styled-components"
import { boxShadow } from "src/assets/styles"
import { color, font } from "@smoothincorg/smooth-ui"
import { Wrapper } from "src/components/page-directory/Wrapper"
import { Button } from "src/components/Button"

type PdfItem = {
  key: string
  filename: string
  size: number
  lastModified: string
  datePath: string
  downloadUrl: string | null
}

type ContainerProps = {
  pdfItems: PdfItem[]
  onDownload: (key: string) => void
  isNewItem?: (key: string) => boolean
}

type Props = ContainerProps & {
  className?: string
}

const Component: React.VFC<Props> = (props) => {
  // ISO 8601形式の日時をフォーマット
  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // ファイルサイズをフォーマット
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  return (
    <Wrapper className={props.className}>
      <div className={`${props.className}-header`}>
        <h2>PDFファイル一覧</h2>
      </div>

      <ul className={`${props.className}-pdfs`}>
        {props.pdfItems.map((item) => {
          const isNew = props.isNewItem?.(item.key) || false
          return (
            <li
              key={item.key}
              className={`${props.className}-pdfs__item ${
                isNew ? `${props.className}-pdfs__item--new` : ""
              }`}
            >
              <div className={`${props.className}-pdf-content`}>
                <div className={`${props.className}-pdf-info`}>
                  <div className={`${props.className}-pdf-item`}>
                    <span className={`${props.className}-pdf-item__title`}>ファイル名</span>
                    <span className={`${props.className}-pdf-item__content`}>{item.filename}</span>
                  </div>
                  <div className={`${props.className}-pdf-item`}>
                    <span className={`${props.className}-pdf-item__title`}>作成日時</span>
                    <span className={`${props.className}-pdf-item__content`}>
                      {formatDate(item.lastModified)}
                    </span>
                  </div>
                  <div className={`${props.className}-pdf-item`}>
                    <span className={`${props.className}-pdf-item__title`}>ファイルサイズ</span>
                    <span className={`${props.className}-pdf-item__content`}>
                      {formatFileSize(item.size)}
                    </span>
                  </div>
                </div>
                <div className={`${props.className}-pdf-actions`}>
                  <Button onClick={() => props.onDownload(item.key)}>ダウンロード</Button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      {props.pdfItems.length === 0 && (
        <div className={`${props.className}-empty`}>PDFファイルがありません</div>
      )}
    </Wrapper>
  )
}

const StyledComponent = styled(Component)`
  padding: 40px 0;

  &-header {
    margin-bottom: 20px;

    h2 {
      ${font(24)};
      margin: 0;
    }
  }

  &-pdfs {
    &__item {
      margin-bottom: 12px;
      border-radius: 8px;
      width: 100%;
      ${boxShadow.deep};
      padding: 16px 18px;
      transition: background-color 0.3s ease;

      &--new {
        background-color: #e8f5e8;
        border: 2px solid ${color.main};
        animation: pulse 2s infinite;
      }
    }
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
    }
  }

  &-pdf-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &-pdf-info {
    display: flex;
    gap: 30px;
    flex: 1;
  }

  &-pdf-item {
    display: flex;
    flex-direction: column;

    &__title {
      ${font(13)};
      color: ${color.darkGray};
      margin-bottom: 4px;
    }

    &__content {
      ${font(15)};
      font-weight: bold;
    }
  }

  &-pdf-actions {
    margin-left: 20px;

    button {
      padding: 10px 20px;
      font-size: 14px;
      font-weight: bold;
      min-height: 40px;
      min-width: 120px;
    }
  }

  &-empty {
    text-align: center;
    padding: 40px;
    ${font(16)};
    color: ${color.darkGray};
  }
`

const Container: React.FC<ContainerProps> = (props) => {
  return <StyledComponent {...props} />
}

export const PdfList = Container
